import React, { useState, useEffect } from 'react';
import { getCommittees, createCommittee, updateCommittee, deleteCommittee, getCommitteeMembers, getUsers, assignUserCommittees, updateCommitteeSupervisors, getCommitteeSupervisors } from '@/lib/api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { FolderKanban, Plus, Edit2, Trash2, Users, UserPlus, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';

export default function CommitteesPage() {
  const [committees, setCommittees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [membersTarget, setMembersTarget] = useState(null);
  const [members, setMembers] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [assignTarget, setAssignTarget] = useState(null);
  const [selectedCommittees, setSelectedCommittees] = useState([]);
  const [supervisorTarget, setSupervisorTarget] = useState(null);
  const [supervisorUsers, setSupervisorUsers] = useState([]);
  const [selectedSupervisors, setSelectedSupervisors] = useState([]);

  // Form state
  const [formName, setFormName] = useState('');
  const [formDesc, setFormDesc] = useState('');

  useEffect(() => { loadCommittees(); }, []);

  const loadCommittees = async () => {
    setLoading(true);
    try {
      const { data } = await getCommittees();
      setCommittees(data.committees || []);
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleCreate = async () => {
    if (!formName.trim()) { toast.error('Name is required'); return; }
    try {
      await createCommittee({ name: formName.trim(), description: formDesc.trim() });
      toast.success('Committee created');
      setShowCreate(false);
      setFormName(''); setFormDesc('');
      loadCommittees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to create'); }
  };

  const handleUpdate = async () => {
    if (!editTarget) return;
    try {
      await updateCommittee(editTarget.committee_id, { name: formName.trim(), description: formDesc.trim() });
      toast.success('Committee updated');
      setEditTarget(null);
      setFormName(''); setFormDesc('');
      loadCommittees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update'); }
  };

  const handleDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteCommittee(deleteTarget.committee_id);
      toast.success('Committee deleted');
      setDeleteTarget(null);
      loadCommittees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to delete'); }
  };

  const viewMembers = async (committee) => {
    setMembersTarget(committee);
    try {
      const { data } = await getCommitteeMembers(committee.committee_id);
      setMembers(data.members || []);
    } catch { setMembers([]); }
  };

  const openAssign = async () => {
    try {
      const { data } = await getUsers({});
      setAllUsers(data.users || []);
      setAssignTarget(true);
    } catch { setAllUsers([]); }
  };

  const startEditUser = (u) => {
    setSelectedCommittees(u.committees || []);
    setAssignTarget(u);
  };

  const toggleCommittee = (name) => {
    setSelectedCommittees(prev =>
      prev.includes(name) ? prev.filter(c => c !== name) : [...prev, name]
    );
  };

  const saveUserCommittees = async () => {
    if (!assignTarget || assignTarget === true) return;
    try {
      await assignUserCommittees(assignTarget.user_id, selectedCommittees);
      toast.success(`Updated committees for ${assignTarget.name}`);
      setAssignTarget(null);
      loadCommittees();
    } catch (e) { toast.error('Failed to update'); }
  };

  const openSupervisorAssign = async (committee) => {
    setSupervisorTarget(committee);
    try {
      const [usersRes, supRes] = await Promise.all([
        getUsers({ role: 'supervisor' }),
        getCommitteeSupervisors(committee.committee_id)
      ]);
      // Include admins too
      const adminRes = await getUsers({ role: 'admin' });
      setSupervisorUsers([...(usersRes.data.users || []), ...(adminRes.data.users || [])]);
      setSelectedSupervisors((supRes.data.supervisors || []).map(s => s.user_id));
    } catch { setSupervisorUsers([]); setSelectedSupervisors([]); }
  };

  const toggleSupervisor = (userId) => {
    setSelectedSupervisors(prev =>
      prev.includes(userId) ? prev.filter(id => id !== userId) : [...prev, userId]
    );
  };

  const saveSupervisors = async () => {
    if (!supervisorTarget) return;
    try {
      await updateCommitteeSupervisors(supervisorTarget.committee_id, selectedSupervisors);
      toast.success(`Supervisors updated for ${supervisorTarget.name}`);
      setSupervisorTarget(null);
      loadCommittees();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed to update supervisors'); }
  };

  return (
    <div className="animate-fade-in" data-testid="committees-page">
      <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8 gap-4">
        <div>
          <h1 className="font-['Manrope'] text-3xl font-bold text-[#2D2B2A] tracking-tight">Committees</h1>
          <p className="text-[#5C5855] mt-1">Manage committees and assign volunteers</p>
        </div>
        <div className="flex gap-3">
          <Button onClick={() => { setFormName(''); setFormDesc(''); setShowCreate(true); }}
            className="bg-[#D3735A] hover:bg-[#BD6047] text-white rounded-lg px-5 py-2.5 font-semibold"
            data-testid="create-committee-button">
            <Plus className="h-4 w-4 mr-2" /> New Committee
          </Button>
          <Button onClick={openAssign} variant="outline"
            className="border-[#3F6352] text-[#3F6352] hover:bg-[#3F6352] hover:text-white rounded-lg px-5 py-2.5 font-semibold"
            data-testid="assign-members-button">
            <UserPlus className="h-4 w-4 mr-2" /> Assign Members
          </Button>
        </div>
      </div>

      {/* Committees Table */}
      <div className="w-full rounded-xl border border-[#E8E3DD] overflow-hidden bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#FAF8F5] border-b border-[#E8E3DD]">
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Name</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Description</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4">Members</TableHead>
              <TableHead className="text-[#5C5855] font-semibold text-xs uppercase tracking-wider px-6 py-4 text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              Array.from({ length: 4 }).map((_, i) => (
                <TableRow key={i} className="border-b border-[#E8E3DD]">
                  <TableCell colSpan={4} className="px-6 py-4"><div className="h-4 bg-[#F2EFEB] rounded animate-pulse" /></TableCell>
                </TableRow>
              ))
            ) : committees.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} className="text-center py-12 text-[#8A847F]">
                  <FolderKanban className="h-8 w-8 mx-auto mb-2 opacity-40" />
                  <p className="text-sm">No committees yet</p>
                </TableCell>
              </TableRow>
            ) : (
              committees.map((c) => (
                <TableRow key={c.committee_id} className="border-b border-[#E8E3DD] hover:bg-[#F2EFEB]/50 transition-colors" data-testid={`committee-row-${c.committee_id}`}>
                  <TableCell className="px-6 py-4 text-sm font-semibold text-[#2D2B2A]">{c.name}</TableCell>
                  <TableCell className="px-6 py-4 text-sm text-[#5C5855]">{c.description || '—'}</TableCell>
                  <TableCell className="px-6 py-4">
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => viewMembers(c)}
                        className="text-[#3F6352] hover:bg-[#3F6352]/10 text-xs"
                        data-testid={`view-members-${c.committee_id}`}>
                        <Users className="h-3.5 w-3.5 mr-1" /> {c.member_count || 0}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => openSupervisorAssign(c)}
                        className="text-[#4A788C] hover:bg-[#4A788C]/10 text-xs"
                        data-testid={`assign-supervisors-${c.committee_id}`}>
                        <ShieldCheck className="h-3.5 w-3.5 mr-1" /> {(c.supervisor_ids || []).length} sups
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="px-6 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <Button variant="ghost" size="sm" onClick={() => { setFormName(c.name); setFormDesc(c.description || ''); setEditTarget(c); }}
                        data-testid={`edit-committee-${c.committee_id}`}>
                        <Edit2 className="h-4 w-4 text-[#5C5855]" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => setDeleteTarget(c)}
                        data-testid={`delete-committee-${c.committee_id}`}>
                        <Trash2 className="h-4 w-4 text-[#B74134]" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="bg-white" data-testid="create-committee-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Create Committee</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Name *</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} className="border-[#D1C9C0]" data-testid="committee-name-input" placeholder="Committee name" />
            </div>
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Description</Label>
              <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="border-[#D1C9C0]" data-testid="committee-desc-input" placeholder="Optional description" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleCreate} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="save-committee-button">Create</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={!!editTarget} onOpenChange={(o) => !o && setEditTarget(null)}>
        <DialogContent className="bg-white" data-testid="edit-committee-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Edit Committee</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Name</Label>
              <Input value={formName} onChange={(e) => setFormName(e.target.value)} className="border-[#D1C9C0]" />
            </div>
            <div>
              <Label className="block text-sm font-semibold text-[#5C5855] mb-2">Description</Label>
              <Textarea value={formDesc} onChange={(e) => setFormDesc(e.target.value)} className="border-[#D1C9C0]" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleUpdate} className="bg-[#D3735A] hover:bg-[#BD6047] text-white">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
        <DialogContent className="bg-white" data-testid="delete-committee-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Delete Committee</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C5855]">Delete <strong>{deleteTarget?.name}</strong>? This cannot be undone.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={handleDelete} className="bg-[#B74134] hover:bg-[#9a3229] text-white" data-testid="confirm-delete-committee">Delete</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Members Dialog */}
      <Dialog open={!!membersTarget} onOpenChange={(o) => !o && setMembersTarget(null)}>
        <DialogContent className="bg-white max-w-lg" data-testid="members-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">{membersTarget?.name} - Members</DialogTitle></DialogHeader>
          {members.length === 0 ? (
            <p className="text-sm text-[#8A847F] text-center py-4">No members assigned yet</p>
          ) : (
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {members.map(m => (
                <div key={m.user_id} className="flex items-center justify-between bg-[#FAF8F5] rounded-lg px-4 py-3 border border-[#E8E3DD]">
                  <div>
                    <p className="text-sm font-semibold text-[#2D2B2A]">{m.name}</p>
                    <p className="text-xs text-[#8A847F]">{m.email} - {m.volunteer_id}</p>
                  </div>
                  <span className="text-xs font-bold text-[#3F6352] capitalize">{m.role}</span>
                </div>
              ))}
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMembersTarget(null)} className="border-[#D1C9C0]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Committees Dialog */}
      <Dialog open={assignTarget === true} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className="bg-white max-w-2xl" data-testid="assign-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Assign Committees to Users</DialogTitle></DialogHeader>
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {allUsers.map(u => (
              <div key={u.user_id} className="flex items-center justify-between bg-[#FAF8F5] rounded-lg px-4 py-3 border border-[#E8E3DD]">
                <div>
                  <p className="text-sm font-semibold text-[#2D2B2A]">{u.name}</p>
                  <p className="text-xs text-[#8A847F]">{u.email} - <span className="capitalize">{u.role}</span></p>
                  <p className="text-xs text-[#5C5855] mt-1">{(u.committees || []).join(', ') || 'No committees'}</p>
                </div>
                <Button size="sm" variant="outline" onClick={() => startEditUser(u)}
                  className="border-[#D3735A] text-[#D3735A] text-xs" data-testid={`edit-user-committees-${u.user_id}`}>
                  Edit
                </Button>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)} className="border-[#D1C9C0]">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Committees Dialog */}
      <Dialog open={assignTarget && assignTarget !== true} onOpenChange={(o) => !o && setAssignTarget(null)}>
        <DialogContent className="bg-white" data-testid="edit-user-committees-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Committees for {assignTarget?.name}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {committees.map(c => (
              <label key={c.committee_id} className="flex items-center gap-3 p-3 bg-[#FAF8F5] rounded-lg border border-[#E8E3DD] cursor-pointer hover:bg-[#F2EFEB] transition-colors">
                <Checkbox
                  checked={selectedCommittees.includes(c.name)}
                  onCheckedChange={() => toggleCommittee(c.name)}
                  data-testid={`checkbox-${c.committee_id}`}
                />
                <span className="text-sm font-medium text-[#2D2B2A]">{c.name}</span>
              </label>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={saveUserCommittees} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="save-user-committees">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supervisor Assignment Dialog */}
      <Dialog open={!!supervisorTarget} onOpenChange={(o) => !o && setSupervisorTarget(null)}>
        <DialogContent className="bg-white" data-testid="supervisor-assign-dialog">
          <DialogHeader><DialogTitle className="font-['Manrope']">Assign Supervisors to {supervisorTarget?.name}</DialogTitle></DialogHeader>
          <p className="text-sm text-[#5C5855] mb-3">Select supervisors/admins for this committee (multiple allowed):</p>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {supervisorUsers.length === 0 ? (
              <p className="text-sm text-[#8A847F] text-center py-4">No supervisors or admins found</p>
            ) : (
              supervisorUsers.map(u => (
                <label key={u.user_id} className="flex items-center gap-3 p-3 bg-[#FAF8F5] rounded-lg border border-[#E8E3DD] cursor-pointer hover:bg-[#F2EFEB] transition-colors">
                  <Checkbox
                    checked={selectedSupervisors.includes(u.user_id)}
                    onCheckedChange={() => toggleSupervisor(u.user_id)}
                    data-testid={`sup-checkbox-${u.user_id}`}
                  />
                  <div>
                    <span className="text-sm font-medium text-[#2D2B2A]">{u.name}</span>
                    <span className="text-xs text-[#8A847F] ml-2">({u.role})</span>
                  </div>
                </label>
              ))
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSupervisorTarget(null)} className="border-[#D1C9C0]">Cancel</Button>
            <Button onClick={saveSupervisors} className="bg-[#D3735A] hover:bg-[#BD6047] text-white" data-testid="save-supervisors-btn">Save Supervisors</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
